import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreOrderModal = ({ isOpen, onClose, product }) => {
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/preorders`, {
        product_id: product.id,
        product_name: product.name,
        customer_name: formData.customer_name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message || null,
      });

      toast.success("Enquiry submitted successfully! Check your email for confirmation.");
      setFormData({ customer_name: "", email: "", phone: "", message: "" });
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit enquiry");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ customer_name: "", email: "", phone: "", message: "" });
    onClose();
  };

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="preorder-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col shadow-2xl">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors z-10"
          data-testid="close-modal-btn"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Scrollable Content */}
        <div className="p-6 pb-0 overflow-y-auto flex-1">
          {/* Header */}
          <div className="mb-4">
            <h2 className="font-heading text-2xl font-bold text-foreground">Enquiry Request</h2>
            <p className="text-muted-foreground text-sm mt-1">
              Fill in your details and we&apos;ll contact you soon.
            </p>
          </div>

          {/* Product Preview */}
          <div className="flex gap-4 p-4 bg-muted/50 rounded-xl mb-6">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
            />
            <div className="min-w-0">
              <h4 className="font-heading font-semibold text-foreground truncate">{product.name}</h4>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" id="enquiry-form">
            <div className="space-y-2">
              <Label htmlFor="customer_name">Your Name *</Label>
              <Input
                id="customer_name"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                placeholder="Enter your full name"
                className="h-11 rounded-lg bg-muted/50 border-transparent focus:border-primary focus:bg-background"
                data-testid="input-customer-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="h-11 rounded-lg bg-muted/50 border-transparent focus:border-primary focus:bg-background"
                data-testid="input-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number (Optional)</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
                className="h-11 rounded-lg bg-muted/50 border-transparent focus:border-primary focus:bg-background"
                data-testid="input-phone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message (Optional)</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Any specific requirements?"
                rows={2}
                className="rounded-lg bg-muted/50 border-transparent focus:border-primary focus:bg-background resize-none"
                data-testid="input-message"
              />
            </div>
          </form>
        </div>

        {/* Sticky Footer with Buttons */}
        <div className="p-6 pt-4 border-t border-border/50 bg-card rounded-b-2xl">
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1 h-12 rounded-full"
              data-testid="cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="enquiry-form"
              disabled={loading}
              className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
              data-testid="submit-preorder-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Submit Request"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreOrderModal;
